import { WorkspaceLoadError, WorkspaceNotFoundError, WorkspacePermissionError } from './errors';
import type { Workspace } from '@thinkalpha/ui-types/contracts/workspace';
import { isAxiosError } from 'axios';
import { stripUnrecognizedWidgets } from 'src/lib/widget';
import { allWidgets } from 'src/lib/widget/widgets';
import { getWorkspaceBundleQuery, hydrateWorkspaceBundle } from 'src/queries/workspace';
import type { LocalWidgetStorageService } from 'src/services/LocalWidgetStorageService';
import type { WorkspaceViewModel } from 'src/store/types';
import { createWorkspaceViewModel } from 'src/store/types/workspaceViewModels';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Result of fetching and pre-cleaning a workspace for load. Returns just
 * the workspace VM (Redux's workspace metadata) plus the cleaned blob in
 * its contract shape — the canonical source the caller fans out from.
 *
 * Containers / layouts / widget-tab view-models are derivable from
 * `cleanedWorkspace` and are constructed by the caller (currently
 * {@link WorkspaceServiceImpl.load}) so per-tab discrimination between
 * legacy and self-owned widgets stays in one place.
 */
type WorkspaceLoadResult = {
    workspaceViewModel: WorkspaceViewModel;
    cleanedWorkspace: Workspace;
};

export async function getWorkspaceViewModelSetup(
    workspaceId: 'default' | string,
    queryClient: ReactBindings['QueryClient'],
    localWidgetStorageService: LocalWidgetStorageService,
): Promise<WorkspaceLoadResult> {
    let bundle;
    try {
        bundle = await queryClient.fetchQuery(getWorkspaceBundleQuery(workspaceId));
    } catch (e) {
        if (isAxiosError(e)) {
            if (e.response?.status === 403) throw new WorkspacePermissionError();
            if (e.response?.status === 404) throw new WorkspaceNotFoundError();
        }
        throw new WorkspaceLoadError(e);
    }

    hydrateWorkspaceBundle(queryClient, bundle);

    const { workspace } = bundle;

    // Load and merge local widgets from localStorage
    const localContainers = localWidgetStorageService.loadLocalWidgets(workspace.id);

    // Build a set of valid local-only widget types for cleanup
    const validLocalWidgetTypes = new Set(
        Object.entries(allWidgets)
            // Keep legacy local Alerts widgets around until the next workspace save migrates them.
            .filter(([type, definition]) => definition.isLocalOnly || type === 'alerts')
            .map(([type]) => type),
    );

    // Cleanup stale widgets (types that are no longer marked as local-only)
    localWidgetStorageService.cleanupStaleWidgets(workspace.id, validLocalWidgetTypes);

    // Merge local tabs back into their original containers
    const localContainersByContainerId = new Map(localContainers.map((lc) => [lc.containerId, lc] as const));

    const mergedContainers = workspace.containers.map((container) => {
        const localContainer = localContainersByContainerId.get(container.id);
        if (localContainer) {
            // Merge local tabs into this container
            const mergedTabs = [...container.tabs, ...localContainer.tabs];
            const shouldUseLocalActiveTab =
                localContainer.activeTabId && localContainer.tabs.some((tab) => tab.id === localContainer.activeTabId);

            return {
                ...container,
                tabs: mergedTabs,
                activeTabId: shouldUseLocalActiveTab ? localContainer.activeTabId : container.activeTabId,
            };
        }
        return container;
    });

    // Add any local containers that don't exist in the workspace (fully local containers)
    const existingContainerIds = new Set(workspace.containers.map((c) => c.id));
    const newLocalContainers = localContainers
        .filter((lc) => !existingContainerIds.has(lc.containerId))
        .map((lc) => ({
            id: lc.containerId,
            tabs: lc.tabs,
            layout: lc.layout,
            activeTabId: lc.activeTabId,
        }));

    const mergedWorkspace: Workspace = {
        ...workspace,
        containers: [...mergedContainers, ...newLocalContainers],
    };

    // Strip unrecognized widgets and remove empty containers before
    // returning. Per-container/per-tab fan-out (containerViewModels,
    // layoutViewModels, widgetTabViewModels) lives in the caller so
    // self-owned vs legacy widget discrimination stays in one place.
    const cleanedWorkspace = stripUnrecognizedWidgets(mergedWorkspace);

    const workspaceViewModel = createWorkspaceViewModel(cleanedWorkspace, workspace.isUserDefault);

    return { workspaceViewModel, cleanedWorkspace };
}
